import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core"
import { Form, Formik } from "formik"
import React, { useContext, useState } from "react"
import { useQueryClient } from "react-query"
import * as Yup from "yup"
import AvatarUpload from "~/components/Common/AvatarUpload"
import CounterHeader from "~/components/Common/CounterHeader"
import LoaderButton from "~/components/LoaderButton/index.jsx"
import App from "~/contexts/App"
import InstructorsContext from "~/contexts/Instructors"
import useOpenErrorSnackbar from "~/hooks/useOpenErrorSnackbar"
import serialize from "~/store/serialize"

import Box from "~/components/Common/Box"
import PermissionsWrapper from "~/components/Common/PermissionsWrapper"
import useStyles from "./styles"

const EditInstructorDialog = () => {
  const classes = useStyles()
  const theme = useTheme()
  const queryClient = useQueryClient()
  const { dialogs, closeDialog, openDialog, openSnackBar, authUser } =
    useContext(App)
  const { deleteInstructorMutation, instructors } =
    useContext(InstructorsContext)
  const thisDialog = dialogs?.["editInstructorDialog"] || {}
  const { open = false, data } = thisDialog
  const openErrorSnackbar = useOpenErrorSnackbar()
  const smDown = useMediaQuery(theme.breakpoints.down("sm"))

  const instructorId = thisDialog?.data?.id
  const updateMutation = thisDialog?.data?.update
  const instructorPassed = thisDialog?.data?.instructor

  //finds instructor that was clicked or instructor that was passed from InstructorGroup
  const instructor = instructorPassed
    ? instructorPassed
    : instructors?.find((x) => x.id === Number(instructorId))

  const [imageLoading, setImageLoading] = useState(false)
  const [uploadRes, setUploadRes] = useState(null)

  const schema = Yup.object().shape({
    name: Yup.string()
      .required(`Please enter the instructor's name`)
      .max(100, "Name must be less than 100 characters"),
    title: Yup.string()
      //.required(`Please enter the instructor's title`),
      .max(100, "Title must be less than 100 characters"),
    bio: Yup.string()
      //.required(`Please enter the instructor's bio`),
      .max(1000, "Bio must be less than 1000 characters"),
    // avatar not required at this time
  })

  const handleClose = () => {
    closeDialog("editInstructorDialog")
    setTimeout(() => {
      setUploadRes(null)
    }, 100)
  }

  const handleRemove = () => {
    let data = {
      title: "Remove an Instructor",
      subtitle: "",
      type: "instructor",
      delete: deleteInstructorMutation,
      id: instructorId,
      confirmWord: "delete",
    }
    openDialog("confirmationTypingDialog", data).then(() => handleClose())
  }

  // fn that submits values from fields/avatar upload and handles response
  const onSubmit = (values, actions) => {
    const data = {
      name: values.name,
      title: values.title,
      bio: values.bio,
      /* eslint-disable */
      //will send the upload response object if there is one, if not sends the instructor profile_image object
      profile_image: uploadRes
        ? JSON.stringify({
            url: uploadRes.data.url,
            secure_url: uploadRes.data.secure_url,
            originial_filename: uploadRes.data.original_filename,
            version: uploadRes.data.version,
            format: uploadRes.data.format,
            public_id: uploadRes.data.public_id,
          })
        : JSON.stringify(values.avatar),
      /* eslint-enable */
    }
    updateMutation.mutate(
      { values: data, id: instructorId },
      {
        onSuccess: (res) => {
          serialize("instructor", res.data[0]).then((serializedData) => {
            queryClient.setQueryData(["instructors", authUser], (oldData) => {
              let newArray = []
              newArray = oldData.map((x) => {
                if (x.id !== serializedData.id) {
                  return x
                } else {
                  return serializedData
                }
              })
              return [...newArray]
            })
          })
          openSnackBar({ message: "Instructor Updated" })
          handleClose()
        },
        onError: (err) => {
          console.error(err)
          openErrorSnackbar(err, "Error Editing Instructor")
        },
        onSettled: () => {
          actions.setSubmitting(false)
          handleClose()
        },
      },
    )
  }

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullScreen={smDown}
      onClose={() => closeDialog("editInstructorDialog", false)}
    >
      <DialogTitle disableTypography>
        <Typography color="primary" variant="h3" style={{ fontWeight: 600 }}>
          Edit Instructor
        </Typography>
      </DialogTitle>
      <Formik
        initialValues={{
          name: instructor?.name,
          title: instructor?.title,
          bio: instructor?.bio,
          avatar: instructor?.profile_image,
        }}
        onSubmit={onSubmit}
        validationSchema={schema}
      >
        {({
          handleChange,
          values,
          errors,
          touched,
          handleBlur,
          isSubmitting,
          handleSubmit,
        }) => (
          <Form onSubmit={handleSubmit}>
            <DialogContent className={classes.contentContainer}>
              <Box className={classes.topContainer}>
                <Box className={classes.leftContainer}>
                  <AvatarUpload
                    cloudinaryUploadPreset={
                      import.meta.env.VITE_CLOUDINARY_CLOUD_THUMBNAIL_UPLOAD
                    }
                    uploadRes={uploadRes}
                    setUploadRes={setUploadRes}
                    value={values.avatar}
                    imageLoading={imageLoading}
                    setImageLoading={setImageLoading}
                  />
                </Box>
                <Box className={classes.rightContainer}>
                  <CounterHeader
                    title={" " + "Name"}
                    value={values.name}
                    max={100}
                  />
                  <TextField
                    type="text"
                    name="name"
                    id="name"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    helperText={
                      <span>
                        {touched.name && errors.name ? errors.name : " "}
                      </span>
                    }
                    /* eslint-disable-next-line */
                    autoFocus
                    FormHelperTextProps={{
                      className: classes.helper,
                    }}
                    value={values.name}
                    variant="filled"
                    fullWidth
                    InputProps={{
                      classes: {
                        root: classes.inputBackgroundColor,
                        disabled: classes.disabled,
                      },
                    }}
                    className={classes.textField}
                    error={!!touched.name && !!errors.name}
                  />
                  <CounterHeader
                    title={" " + "Title"}
                    value={values.title}
                    max={100}
                  />
                  <TextField
                    type="text"
                    name="title"
                    id="title"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    helperText={
                      <span>
                        {touched.title && errors.title ? errors.title : " "}
                      </span>
                    }
                    FormHelperTextProps={{
                      className: classes.helper,
                    }}
                    value={values.title}
                    variant="filled"
                    fullWidth
                    InputProps={{
                      classes: {
                        root: classes.inputBackgroundColor,
                        disabled: classes.disabled,
                      },
                    }}
                    className={classes.textField}
                    error={!!touched.title && !!errors.title}
                  />
                </Box>
              </Box>
              <CounterHeader
                title={" " + "Bio"}
                value={values.bio}
                max={1000}
              />
              <TextField
                type="text"
                name="bio"
                id="bio"
                onChange={handleChange}
                onBlur={handleBlur}
                helperText={
                  <span>{touched.bio && errors.bio ? errors.bio : " "}</span>
                }
                size="small"
                value={values.bio}
                fullWidth
                multiline
                maxRows={12}
                InputProps={{
                  disableUnderline: true,
                  classes: {
                    root: classes.inputBio,
                    disabled: classes.disabled,
                  },
                }}
                error={!!touched.bio && !!errors.bio}
              />
            </DialogContent>
            <DialogActions>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
                margin="16px"
                className={classes.buttonsContainer}
              >
                <Box>
                  <PermissionsWrapper permissionGroup="admin">
                    <Button
                      className={classes.remove}
                      onClick={() => handleRemove()}
                    >
                      Remove Instructor
                    </Button>
                  </PermissionsWrapper>
                </Box>
                <Box>
                  <Button
                    className={classes.cancel}
                    onClick={() => handleClose()}
                  >
                    Cancel
                  </Button>
                  <PermissionsWrapper permissionGroup="admin">
                    <LoaderButton
                      // onClick={() => saveUpdate()}
                      type="submit"
                      variant="contained"
                      color="secondary"
                      working={isSubmitting}
                      disabled={imageLoading || isSubmitting}
                    >
                      save
                    </LoaderButton>
                  </PermissionsWrapper>
                </Box>
              </Box>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}

export default EditInstructorDialog
